import { Check, ChevronDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { SearchInput } from '../../../../components/Input';
import { Popover, usePopover } from '../../../../components/Popover';
import { fuzzyFilter } from '../../../../lib/fuzzy';
import styles from './FilterPill.module.css';

// One filter of the Event log's row (Event log A): a pill reading "Event type: Any", whose choices open beside it
// (CLAUDE.md §5), never a stock <select>. A long list gets a filter box, as the app's Select does.

export interface PillOption {
  readonly value: string;
  readonly label: string;
  /** Small code letters under the label, e.g. the event type's technical name. */
  readonly code?: string;
}

/** Longer than this, the list gets its own filter box rather than a long scroll (as Select's). */
const FILTER_THRESHOLD = 8;

interface FilterPillProps {
  /** "Event type" — the pill reads "Event type: Any" until something is picked. */
  readonly label: string;
  readonly anyLabel: string;
  readonly options: readonly PillOption[];
  readonly value: string | undefined;
  readonly onChange: (value: string | undefined) => void;
  readonly icon?: ReactNode;
}

export function FilterPill({ label, anyLabel, options, value, onChange, icon }: FilterPillProps): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const [query, setQuery] = useState('');
  const picked = options.find((option) => option.value === value);
  const shown = query.trim() === '' ? options : fuzzyFilter(query, options, (option) => `${option.label} ${option.code ?? ''}`).map((result) => result.item);

  const pick = (next: string | undefined): void => {
    onChange(next);
    setQuery('');
    close();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.pill}
        data-set={value !== undefined || undefined}
        aria-haspopup="listbox"
        aria-expanded={anchor !== null}
        onClick={toggle}
      >
        {icon}
        <span className={styles.text}>
          {label}: <b>{picked?.label ?? (value ? value : anyLabel)}</b>
        </span>
        <ChevronDown size={14} aria-hidden />
      </button>
      <Popover anchor={anchor} onClose={close} label={label}>
        <div className={styles.panel}>
          {options.length > FILTER_THRESHOLD && (
            <SearchInput aria-label={`Find ${label.toLowerCase()}`} placeholder={`Find ${label.toLowerCase()}`} value={query} onChange={(event) => setQuery(event.target.value)} onClear={() => setQuery('')} autoFocus />
          )}
          <ul className={styles.list} role="listbox" aria-label={label}>
            <li>
              <Choice label={anyLabel} isOn={value === undefined} onPick={() => pick(undefined)} />
            </li>
            {shown.map((option) => (
              <li key={option.value}>
                <Choice label={option.label} code={option.code} isOn={option.value === value} onPick={() => pick(option.value)} />
              </li>
            ))}
            {shown.length === 0 && <li className={styles.none}>Nothing matches “{query}”.</li>}
          </ul>
        </div>
      </Popover>
    </>
  );
}

function Choice({ label, code, isOn, onPick }: { readonly label: string; readonly code?: string | undefined; readonly isOn: boolean; readonly onPick: () => void }): ReactNode {
  return (
    <button type="button" role="option" aria-selected={isOn} className={styles.choice} onClick={onPick}>
      <span className={styles.choiceText}>
        <span>{label}</span>
        {code && <span className={styles.code}>{code}</span>}
      </span>
      {isOn && <Check size={14} aria-hidden />}
    </button>
  );
}
